import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { SettingsModal } from '../components/SettingsModal';
import { DWA } from '../theme/dwaTokens';

/**
 * User menu shared by the entryway and the Hub header: name, Settings,
 * Lupa Admin (admins), Sign out.
 */
export const UserMenu: React.FC<{ variant?: 'dark' | 'light' }> = ({ variant = 'dark' }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const name = user?.display_name || user?.email || 'User';
  const initial = name.trim().charAt(0).toUpperCase() || '?';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={open}
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          border: `1px solid ${variant === 'dark' ? 'rgba(254,195,92,0.45)' : 'rgba(255,255,255,0.35)'}`,
          background: DWA.gold,
          color: DWA.onGold,
          fontWeight: 700,
          fontSize: '0.85rem',
          cursor: 'pointer',
          fontFamily: DWA.font,
        }}
      >
        {initial}
      </button>
      {open && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: 'calc(100% + 0.5rem)',
            right: 0,
            minWidth: 200,
            background: '#ffffff',
            borderRadius: DWA.radius,
            boxShadow: DWA.cardShadow,
            border: `1px solid ${DWA.cardBorder}`,
            padding: '0.4rem',
            zIndex: 200,
          }}
        >
          <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: DWA.textSecondary, wordBreak: 'break-word' }}>
            {name}
          </div>
          <MenuItem
            label="Settings"
            onClick={() => {
              setOpen(false);
              setSettingsOpen(true);
            }}
          />
          {user?.role === 'dwa_admin' && (
            <MenuItem
              label="Lupa Admin ↗"
              onClick={() => {
                setOpen(false);
                window.open('/lupa-admin', '_blank');
              }}
            />
          )}
          <MenuItem
            label="Sign out"
            onClick={async () => {
              setOpen(false);
              await logout();
              navigate('/login', { replace: true });
            }}
          />
        </div>
      )}
      <SettingsModal isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} user={user} />
    </div>
  );
};

const MenuItem: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    role="menuitem"
    onClick={onClick}
    style={{
      display: 'block',
      width: '100%',
      padding: '0.55rem 0.75rem',
      background: 'none',
      border: 'none',
      borderRadius: DWA.radius,
      textAlign: 'left',
      fontSize: '0.875rem',
      color: DWA.ink,
      cursor: 'pointer',
      fontFamily: DWA.font,
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.backgroundColor = DWA.goldSoft;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.backgroundColor = 'transparent';
    }}
  >
    {label}
  </button>
);
